import React, { useEffect, useState } from "react";
import { API } from "@/lib/constants";
import { Loader2, Mail, ShieldCheck } from "lucide-react";

// ADMIN — Preview Outbox. Reads what the guard CAPTURED instead of sending, so a person
// can read exactly what would have gone out, inside Rex. Nothing here ever left the system.
export default function PreviewEmails() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin/preview-emails`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData).catch((e) => setError(e.message));
  }, []);

  const openOne = (id) => {
    setOpen(id); setDetail(null);
    fetch(`${API}/admin/preview-emails/${id}`, { credentials: "include" })
      .then((r) => r.json()).then(setDetail).catch(() => {});
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="preview-emails-page">
      <div className="mb-6 flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Preview outbox</h1>
          <p className="mt-1 text-sm text-slate-500">What Rex would have emailed — captured, not sent. Nothing here left the system.</p>
        </div>
        {data && (
          <span className={`ml-auto flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${data.capturing ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
            <ShieldCheck className="h-3.5 w-3.5" /> {data.capturing ? `Capturing (${data.environment})` : "Production — live sending"}
          </span>
        )}
      </div>

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="preview-emails-error">Couldn't load: {error}</div>}
      {!data && !error && <div className="flex items-center gap-2 py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}
      {data && data.captures.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center text-slate-400" data-testid="preview-emails-empty">
          Nothing captured yet.
        </div>
      )}

      <div className="space-y-2">
        {data && data.captures.map((c) => (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-white" data-testid="preview-email-row">
            <button onClick={() => openOne(c.id)} className="flex w-full items-center gap-3 px-5 py-4 text-left hover:bg-slate-50">
              <Mail className="h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-900">{c.subject || "(no subject)"}</div>
                <div className="mt-0.5 truncate text-sm text-slate-500">
                  Would have gone to: {(c.intended_recipients || []).join(", ") || "unknown"} · {c.captured_at?.slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">captured</span>
            </button>
            {open === c.id && (
              <div className="border-t border-slate-200 p-4" data-testid="preview-email-detail">
                {!detail ? <div className="flex items-center gap-2 py-6 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading message…</div> : (
                  <>
                    <div className="mb-3 text-xs text-slate-500">From: {detail.from_email || "—"} · To (intended): {(detail.intended_recipients || []).join(", ") || "unknown"}</div>
                    {detail.html
                      ? <iframe title="captured-email" data-testid="preview-email-html" srcDoc={detail.html} className="h-96 w-full rounded-lg border border-slate-200 bg-white" sandbox="" />
                      : <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{detail.text || "(no body)"}</pre>}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
